import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  Sparkles,
  Paperclip,
  FileText,
  Image as ImageIcon,
  Loader2,
  Trash2,
  AlertCircle,
} from "lucide-react";
import { aiWriteCampaign, uploadAiAttachment } from "../api/communicationsApi";

/**
 * "Write with GeeVee" - the brief box, the image/PDF drop zone and Generate.
 *
 * Lifted out of the Campaign editor so the Find Buyers composer can have the
 * same thing (CG, 2026-08-28: "Please copy the same function you have designed
 * already like the below in the communications tab"). Same component, same
 * endpoint, same behaviour on both pages - the alternative was a second, slowly
 * diverging copy of 200 lines of drop-zone handling.
 *
 * It owns the brief and the attachments; the caller owns what to do with the
 * copy that comes back, since a campaign writes it into subject/preview/design
 * and a Find Buyers blast writes it into a modal.
 */
const AI_FILE_OK = /\.(png|jpe?g|gif|webp|pdf)$/i;

export default function AiWritePanel({
  channel = "email",
  onWritten,
  audienceHint = "",
  supportEmail = "",
  placeholder = 'e.g. "Write an email to investors interested in Mill Park explaining why yields are improving"',
  compact = false,
  // A brief the caller wants pre-filled (the Campaign Manager hand-off arrives
  // with one). Only ever seeds an empty box - it must not overwrite what the
  // agent has since typed.
  initialBrief = "",
  // The property the composer is about, when there is one (Find Buyers is
  // always about one listing; the Campaign editor is once a property is
  // picked). The backend turns it into the buyer-portal /p/<slug> link and
  // hands it to GeeVee as the CTA, so a property email links to the portal
  // listing instead of a made-up website page that 404s (Michael, 2026-09-15).
  propertyId = null,
}) {
  const isEmail = channel !== "sms";
  const [brief, setBrief] = useState(initialBrief || "");
  const [busy, setBusy] = useState(false);
  // Images / PDFs dropped on the box. A PDF is read for its text and becomes the
  // source material GeeVee writes from; an image is hosted and placed at the top
  // of the generated email.
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileSeq = useRef(0);
  const fileInputRef = useRef(null);
  // Object URLs for the local thumbnails, revoked on unmount so we don't leak.
  const previewUrls = useRef([]);

  useEffect(() => () => {
    previewUrls.current.forEach((u) => URL.revokeObjectURL(u));
    previewUrls.current = [];
  }, []);

  useEffect(() => {
    if (initialBrief) setBrief((b) => b || initialBrief);
  }, [initialBrief]);

  const addFiles = async (fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    const usable = picked.filter(
      (f) => f.type === "application/pdf" || f.type.startsWith("image/") || AI_FILE_OK.test(f.name)
    );
    if (usable.length < picked.length) toast.error("Only images and PDFs can go here");

    for (const file of usable) {
      const localId = ++fileSeq.current;
      const isImage = file.type.startsWith("image/") || /\.(png|jpe?g|gif|webp)$/i.test(file.name);
      const preview = isImage ? URL.createObjectURL(file) : "";
      if (preview) previewUrls.current.push(preview);
      setFiles((prev) => [
        ...prev,
        { localId, name: file.name, kind: isImage ? "image" : "pdf", preview, uploading: true },
      ]);
      try {
        const res = await uploadAiAttachment(file);
        setFiles((prev) => prev.map((f) => (f.localId === localId ? { ...f, ...res, uploading: false } : f)));
        if (res.warning) toast(res.warning, { icon: "⚠️", duration: 6000 });
      } catch (e) {
        setFiles((prev) => prev.filter((f) => f.localId !== localId));
        if (preview) URL.revokeObjectURL(preview);
        toast.error(e?.response?.data?.detail || `Couldn't read ${file.name}`);
      }
    }
  };

  const removeFile = (localId) => {
    setFiles((prev) => {
      const gone = prev.find((f) => f.localId === localId);
      if (gone?.preview) URL.revokeObjectURL(gone.preview);
      return prev.filter((f) => f.localId !== localId);
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer?.files);
  };

  // Snip-and-paste: a screenshot pasted into the brief box attaches itself.
  const onPaste = (e) => {
    const pasted = Array.from(e.clipboardData?.files || []);
    if (pasted.length) {
      e.preventDefault();
      addFiles(pasted);
    }
  };

  const uploading = files.some((f) => f.uploading);
  const imageCount = files.filter((f) => f.kind === "image" && !f.uploading).length;

  const run = async () => {
    if (!brief.trim()) return toast.error("Describe what you want to say");
    if (uploading) return toast.error("Hang on - still reading your file");
    setBusy(true);
    try {
      const res = await aiWriteCampaign({
        channel,
        brief,
        audience_hint: audienceHint,
        support_email: supportEmail,
        // Let the backend resolve the portal listing link for the CTA.
        ...(propertyId ? { property_id: propertyId } : {}),
        // PDFs give GeeVee the facts to write from; images are placed in the
        // email. Anything still uploading is left out of this run.
        attachments: files
          .filter((f) => !f.uploading)
          .map(({ kind, name, url, text }) => ({ kind, name, url, text })),
      });
      onWritten && onWritten(res);
      if (res.ai_generated === false) {
        toast("GeeVee (AI) isn't set up here - inserted a blank template. Edit or generate the real copy before sending.", { icon: "⚠️", duration: 6000 });
      } else {
        toast.success("GeeVee wrote a draft");
      }
    } catch {
      toast.error("AI write failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#eefcfd] to-white rounded-xl border border-[#bfeef2] p-4">
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-9 h-9 rounded-full bg-white border border-[#bfeef2] shadow-sm overflow-hidden flex items-center justify-center shrink-0">
          <img src="/GeeVee-head.svg" alt="GeeVee" className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0">
          <p className="font-bold text-gray-800 text-sm flex items-center gap-1.5">
            Write with GeeVee <Sparkles className="w-3.5 h-3.5 text-[#55C5D0]" />
          </p>
          <p className="text-[11px] text-gray-500">
            Describe the message, or add a photo or PDF and GeeVee will write from it.
          </p>
        </div>
      </div>
      <textarea
        value={brief}
        onChange={(e) => setBrief(e.target.value)}
        onPaste={onPaste}
        rows={2}
        data-gramm="false"
        data-gramm_editor="false"
        data-enable-grammarly="false"
        placeholder={placeholder}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
      />

      {/* Image / PDF drop. A PDF becomes the source material GeeVee writes
          from; an image is placed at the top of the generated email. */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click(); }}
        className={`mt-2 rounded-lg border-2 border-dashed px-3 ${compact ? "py-2" : "py-3"} text-center cursor-pointer transition-colors ${
          dragOver ? "border-[#55C5D0] bg-[#e6fafd]" : "border-[#bfeef2] bg-white/70 hover:bg-white"
        }`}
      >
        <div className="flex items-center justify-center gap-2 text-xs text-gray-600">
          <Paperclip className="w-4 h-4 text-[#25AFC0]" />
          <span>
            <span className="font-bold text-[#25AFC0]">Add an image or PDF</span>{" "}
            - drop it here, paste it, or click to browse
          </span>
        </div>
        <p className="text-[11px] text-gray-400 mt-1">
          PDFs are read for their wording. Images are added to the top of the email. Max 10MB each.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
          multiple
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
        />
      </div>

      {files.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {files.map((f) => (
            <div
              key={f.localId}
              className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1.5 pr-2 max-w-full"
            >
              {f.kind === "image" ? (
                f.preview ? (
                  <img src={f.preview} alt="" className="w-10 h-10 rounded-md object-cover shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center shrink-0">
                    <ImageIcon className="w-4 h-4 text-gray-400" />
                  </div>
                )
              ) : (
                <div className="w-10 h-10 rounded-md bg-[#eefcfd] flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4 text-[#25AFC0]" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-800 truncate max-w-[160px]">{f.name}</p>
                <p className="text-[11px] text-gray-400 truncate max-w-[160px]">
                  {f.uploading
                    ? "Reading…"
                    : f.kind === "pdf"
                    ? f.chars
                      ? `${f.chars.toLocaleString()} characters read`
                      : "No readable text"
                    : "Added to the email"}
                </p>
              </div>
              {f.uploading ? (
                <Loader2 className="w-4 h-4 text-gray-400 animate-spin shrink-0" />
              ) : (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeFile(f.localId); }}
                  className="text-gray-400 hover:text-red-600 shrink-0"
                  title="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!isEmail && imageCount > 0 && (
        <p className="mt-2 text-[11px] text-amber-600 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5" />
          A text message can't carry a picture - your image is ignored on SMS.
        </p>
      )}

      <button
        onClick={run}
        disabled={busy || uploading}
        className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#55C5D0] text-white text-sm font-bold disabled:opacity-50"
      >
        <Sparkles className="w-4 h-4" /> {busy ? "Writing…" : "Generate"}
      </button>
    </div>
  );
}
